import React, { useState, useEffect, useRef } from "react";
import { Grid3x3, User } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const Header = ({ onToggleSidebar }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [permissions, setPermissions] = useState([]); // store user perms
  const navigate = useNavigate();

  const menuRef = useRef(null);
  const profileRef = useRef(null);

  const handleLogout = () => {
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("permissions");
    navigate("/login", { replace: true });
  };

  // ✅ Load permissions on mount
  useEffect(() => {
    const stored = sessionStorage.getItem("permissions");
    if (stored) {
      setPermissions(JSON.parse(stored));
    }
  }, []);

  // ✅ Menu items with required permissions
  const menuItems = [
    {
      title: "Accounts",
      link: "/users",
      icon: "/img/accounts.png",
      perm: "can_view_user_list",
    },
    {
      title: "Academic",
      link: "/academic",
      icon: "/img/acedemic.png",
      perm: "can_view_academic_page",
    },
    {
      title: "Employees",
      link: "/employees",
      icon: "/img/employees.png",
      perm: "can_view_employee_list",
    },
    {
      title: "Attendance",
      link: "/attendance",
      icon: "/img/attendances.png",
      perm: "can_view_attendance",
    },
    {
      title: "Leave Record",
      link: "/leave",
      icon: "/img/leave.png",
      perm: "can_view_all_leaves",
    },
    {
      title: "Classroom",
      link: "/classroom",
      icon: "/img/classroom.png",
      perm: "can_manage_classroom",
    },
    {
      title: "Inventory",
      link: "/inventory",
      icon: "/img/inventory.png",
      perm: "can_view_inventory",
    },
    {
      title: "Settings",
      link: "/settings",
      icon: "/img/gears.png",
      perm: "can_manage_settings",
    },
  ];

  // ✅ close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 py-1 bg-[#002855] border-b dark:bg-gray-800">
      <div className="flex px-6 items-center justify-between h-full mx-auto">
        {/* Left Title */}
        <div className="flex items-center gap-5">
          <span className="hidden sm:inline text-lg font-semibold text-white">
            Inventory Control
          </span>
        </div>

        {/* Right Section */}
        <ul className="flex items-center flex-shrink-0 space-x-4">
          {/* School Info */}
          <li
            className="hover:bg-gray-700 text-white hover:rounded p-2 cursor-pointer"
            onClick={() => alert("Open Term Modal")}
          >
            <div className="text-end font-semibold hidden sm:block">
              <span className="block text-xs">
                Cambodia Adventist International School
              </span>
              <span className="block text-xs">2025 | Term 1</span>
            </div>
          </li>

          {/* Grid Menu */}
          <li className="relative" ref={menuRef}>
            <button
              className="p-2 mt-1 text-white"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <Grid3x3 size={18} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white border rounded shadow-lg p-4 grid grid-cols-3 gap-1 z-50">
                {menuItems
                  .filter((item) => permissions.includes(item.perm)) // ✅ only allowed
                  .map((item, idx) => (
                    <Link
                      key={idx}
                      to={item.link}
                      className="flex flex-col items-center hover:bg-gray-100 rounded p-2 text-center"
                    >
                      <img src={item.icon} alt="" className="h-8" />
                      <span className="text-gray-700 text-xs">{item.title}</span>
                    </Link>
                  ))}
              </div>
            )}
          </li>

          {/* Profile Menu */}
          <li className="relative" ref={profileRef}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="align-middle rounded-full focus:outline-none"
            >
              <img
                className="object-cover w-8 h-8 rounded-full"
                src="/img/user.png"
                alt="profile"
              />
            </button>
            {profileOpen && (
              <ul className="absolute right-0 w-56 p-2 mt-2 space-y-2 text-gray-600 bg-white border border-gray-100 rounded-md shadow-md">
                <li>
                  <Link
                    to="/profile"
                    className="flex items-center px-2 py-1 text-sm font-semibold rounded-md hover:bg-gray-100"
                  >
                    <User size={16} className="mr-2" /> Profile
                  </Link>
                </li>
                <li>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-2 py-1 text-sm font-semibold rounded-md hover:bg-gray-100"
                  >
                    🔒 Log out
                  </button>
                </li>
              </ul>
            )}
          </li>
        </ul>
      </div>
    </header>
  );
};

export default Header;
